import { MinusIcon, PlusIcon, ReplyIcon } from '@icons';
import { MediaitemModel } from '@models';
import { MediaitemService } from '@services';
import { setupForDark } from '@theme';
import { useNetworkState } from '@uidotdev/usehooks';
import { useGesture } from '@use-gesture/react';
import classNames from 'classnames';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import MediaitemAccessError, { MediaitemErrorCode } from './MediaitemAccessError';

const MediaitemImagePlayer = ({
  mediaitem,
  showThumbnail = false
}: {
  mediaitem: MediaitemModel;
  showThumbnail: boolean;
}) => {
  const mediaitemService = new MediaitemService(mediaitem);
  const src = mediaitemService.getImageSrc(showThumbnail);
  const network = useNetworkState();
  const [accessError, setAccessError] = useState({
    hasAccessError: false,
    errorCode: undefined
  });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const bind = useGesture(
    {
      onPinch: ({ offset: [d] }) => {
        setScale((prevScale) => Math.max(0.5, Math.min(3, prevScale + d / 100)));
      },
      onDrag: ({ offset: [x, y], event }) => {
        event.preventDefault();
        setPosition({ x, y });
      }
    },
    {
      // Optional: Add any configuration options here
    }
  );

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));
  const handleReset = () => setScale(1);

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    setScale((prevScale) => {
      const newScale = prevScale - event.deltaY * 0.001;
      return Math.max(0.5, Math.min(3, newScale));
    });
  };

  const onImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    if (target.naturalWidth === 0 && target.naturalHeight === 0) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED
      });
    } else {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    }
  };

  useEffect(() => {
    setAccessError({
      hasAccessError: false,
      errorCode: undefined
    });
  }, [mediaitem]);

  useEffect(() => {
    if (!network.online) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    }
  }, [network.online]);

  if (accessError.hasAccessError) {
    return (
      <MediaitemAccessError
        type='image'
        errorCode={accessError.errorCode}
        className='w-full h-full'
      />
    );
  }

  return (
    src && (
      <div
        className='relative w-full h-full flex justify-center items-center overflow-hidden'
        {...bind()}
        onWheel={handleWheel}
      >
        <img
          alt='image content'
          src={src}
          onError={onImageError}
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`
          }}
          className={classNames('max-w-full', 'max-h-full', 'object-contain')}
        />
        <div
          className={classNames(
            'absolute bottom-0 flex space-x-2 p-2',
            setupForDark('text-primary')
          )}
        >
          <button onClick={handleZoomIn}>
            <PlusIcon size={24} />
          </button>
          <button onClick={handleZoomOut}>
            <MinusIcon size={24} />
          </button>
          <button onClick={handleReset}>
            <ReplyIcon size={24} />
          </button>
        </div>
      </div>
    )
  );
};

export default MediaitemImagePlayer;
